import type { FC } from 'react';

interface CircularProgressProps {
  progress: number; // 0-100
  size?: 'small' | 'medium' | 'large';
  showPercentage?: boolean;
}

export const CircularProgress: FC<CircularProgressProps> = ({ progress, size = 'medium', showPercentage = true }) => {
  // Size configurations
  const sizes = {
    small: { width: 48, strokeWidth: 4, fontSize: 'text-xs' },
    medium: { width: 64, strokeWidth: 6, fontSize: 'text-sm' },
    large: { width: 96, strokeWidth: 8, fontSize: 'text-lg' },
  };

  const config = sizes[size];
  const radius = (config.width - config.strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (Math.min(100, Math.max(0, progress)) / 100) * circumference;

  return (
    <div className="relative inline-flex items-center justify-center">
      <svg
        width={config.width}
        height={config.width}
        className="transform -rotate-90"
        style={{ filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))' }}
      >
        {/* Background circle */}
        <circle
          cx={config.width / 2}
          cy={config.width / 2}
          r={radius}
          fill="none"
          stroke="#e5e7eb"
          strokeWidth={config.strokeWidth}
        />
        {/* Progress circle */}
        <circle
          cx={config.width / 2}
          cy={config.width / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={config.strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className="text-primary transition-all duration-300 ease-out"
        />
        {/* Spinning animation overlay when progress is 0 or 100 */}
        {(progress === 0 || progress === 100) && (
          <circle
            cx={config.width / 2}
            cy={config.width / 2}
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth={config.strokeWidth}
            strokeDasharray={`${circumference * 0.25} ${circumference * 0.75}`}
            strokeLinecap="round"
            className="text-primary opacity-50 animate-spin"
            style={{ transformOrigin: 'center', animationDuration: '1.5s' }}
          />
        )}
      </svg>
      {/* Percentage text in center */}
      {showPercentage && (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={`${config.fontSize} font-bold text-primary`}>
            {progress === 0 ? '...' : `${Math.min(100, Math.max(0, Math.round(progress)))}%`}
          </span>
        </div>
      )}
    </div>
  );
};
